import React, {useMemo, useState} from 'react';
import type {Optional} from 'utility-types';
import {
	AutoListField,
	type AutoListFieldProps,
} from '@netx/core-components/AutoListField';
import {validateEmail} from '@netx/core-components/Form/validators';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useSnackbar} from '@netx/core-components/Snackbar';
import {splitEventHandler} from '@netx/core-next/utils';
import {EmailFieldError, EmailFieldLabel} from './locales';

const options: string[] = [];

export interface EmailsFieldProps<
	DisableClearable extends boolean = false,
> extends Omit<
	Optional<AutoListFieldProps<string, true, DisableClearable, true>, 'name'>,
	'onSearch' | 'options'
> {}

export const EmailsFieldPrimitive = <DisableClearable extends boolean = false>({
	error,
	onChange,
	...props
}: EmailsFieldProps<DisableClearable>) => {
	const [queryStr, setQueryStr] = useState<string>();
	const snackbar = useSnackbar();
	const [emailAlreadyInList, setEmailAlreadyInList] = useState(false);
	const warning = useMemo(
		() => (emailAlreadyInList ? EmailFieldError.DUPLICATE : undefined),
		[emailAlreadyInList],
	);

	return (
		<AutoListField<string, true, DisableClearable, true>
			clearOnClose
			label={EmailFieldLabel.EMAIL}
			name="email"
			{...props}
			autoSelect
			error={warning ?? error}
			freeSolo
			inputValue={queryStr}
			options={options}
			// until we offer server side lookup
			popupIcon={null}
			getOptionDisabled={useConstCallback((option: string) => {
				return (
					validateEmail(option) !== true ||
					props.getOptionDisabled?.(option) === true
				);
			})}
			onBlur={splitEventHandler(props.onBlur, () =>
				setEmailAlreadyInList(false),
			)}
			// TODO: break out into hook and cleanup
			onChange={(event, value, reason) => {
				const emailAddresses = value
					.map((val) =>
						val
							.split(',')
							.map((v) => v.trim())
							.filter((v) => v.length),
					)
					.flat();
				let validEmailAddresses = emailAddresses.filter(
					(email) => validateEmail(email) === true,
				);
				const findDuplicates = (arr: any[]) =>
					arr.filter((item, index) => arr.indexOf(item) !== index);
				const duplicateEmails = findDuplicates(validEmailAddresses);
				validEmailAddresses = [...new Set(validEmailAddresses)];
				if (duplicateEmails.length) {
					snackbar({
						severity: 'warning',
						message: EmailFieldError.SOME_DUPLICATE(duplicateEmails),
					});
				} else if (emailAddresses.length !== validEmailAddresses.length) {
					snackbar({
						severity: 'warning',
						message: EmailFieldError.SOME_INVALID(
							emailAddresses.filter(
								(email) => !validEmailAddresses.includes(email),
							),
						),
					});
				}
				setQueryStr('');
				onChange?.(event, validEmailAddresses, reason);
			}}
			onInputChange={useConstCallback(
				splitEventHandler(props.onInputChange, (event, value, reason) => {
					setQueryStr(value);
					value !== '' &&
						setEmailAlreadyInList(props.value?.includes(value) === true);
				}),
			)}
		/>
	);
};

/**
 * Field to support adding multiple email addresses.
 */
export const EmailsField = EmailsFieldPrimitive;

export default EmailsField;
